/**
 * window.RendiCartRestore — snapshot the store cart before a Rendi Plus checkout
 * that clears or replaces it, and rebuild it on return. Shared by the cart drawer
 * express activation (rendi-cart-plus.js) and the Rendi Plus landing form
 * (rendi-plus.js), so both back up the cart the same way instead of one silently
 * wiping it.
 *
 * Two activation flows leave the cart empty at checkout:
 *  - drawer: builds a SEPARATE cart via the Storefront API (selling plans can't
 *    ride a permalink) and, for logged-in buyers, Shopify swaps the store cart
 *    for the Plus cart at checkout.
 *  - landing: clears the store cart and adds only the Plus plan.
 * Either way the store lines are gone on return, so we snapshot before leaving
 * and rebuild here. When the cart already matches the snapshot (logged-out drawer
 * buyers keep two independent carts, or the clear never happened) we just drop it.
 *
 * Self-booting: reconciles on load and on bfcache thaw. The cart drawer lives in
 * the global overlay-group, so this runs on every storefront page. Cart fetches
 * go through window.RendiCart (rendi-cart.js, loaded first in the same section):
 * one place that knows the locale-aware routes and the reject-on-HTTP-error POST.
 */
window.RendiCartRestore =
  window.RendiCartRestore ||
  (function () {
    'use strict';

    const RESTORE_KEY = 'rendi:cart-restore';
    let activating = false; // set before we navigate away, so restore won't fire here
    let restoring = false; // re-entrancy guard while a restore is in flight

    // Cart POST that rejects on 4xx/5xx (the gating the destructive restore
    // steps need): RendiCart.mutate with no sections to merge is exactly that.
    function post(name, body) {
      return window.RendiCart.mutate(name, body, []);
    }

    function getCart() {
      return fetch(window.RendiCart.route('get'), { headers: { Accept: 'application/json' } }).then(function (r) {
        return r.json().then(function (data) {
          if (!r.ok) throw data;
          if (!data || !Array.isArray(data.items)) throw new Error('Invalid cart response');
          return data;
        });
      });
    }

    // /cart.js items → /cart/add.js line input (variant id, qty, plan, props).
    function cartLines(cart) {
      return (cart.items || []).map(function (it) {
        const line = { id: it.id, quantity: it.quantity };
        const sp = it.selling_plan_allocation && it.selling_plan_allocation.selling_plan;
        if (sp) line.selling_plan = sp.id;
        if (it.properties && Object.keys(it.properties).length) line.properties = it.properties;
        return line;
      });
    }

    function lineKey(l) {
      return JSON.stringify([l.id, l.selling_plan || 0, l.properties || 0]);
    }

    // True when every saved line is already present in `current` with at least its
    // saved quantity. The Plus swap REPLACES the store cart, so on return the saved
    // lines are missing and we rebuild. A superset instead means the cart only grew
    // (e.g. another tab added an item while the snapshot lingered), so dropping the
    // snapshot without clobbering is the safe choice. Exact match is the logged-out
    // no-op case and is a subset of this.
    function cartHasAll(current, saved) {
      const have = {};
      for (let i = 0; i < current.length; i++) {
        const k = lineKey(current[i]);
        have[k] = (have[k] || 0) + current[i].quantity;
      }
      return saved.every(function (l) {
        return (have[lineKey(l)] || 0) >= l.quantity;
      });
    }

    function clearRestore() {
      try {
        localStorage.removeItem(RESTORE_KEY);
      } catch (e) {}
    }

    // Snapshot lines + cart-level note/attributes. Resolves true ONLY when the
    // snapshot is really persisted: a /cart.js error or a localStorage write that
    // throws (private mode, quota) resolves false, so the caller can bail out of
    // the cart-replacing checkout instead of stranding the customer without a backup.
    function snapshot() {
      return getCart()
        .then(function (cart) {
          const snap = { lines: cartLines(cart), note: cart.note || null, attributes: cart.attributes || null };
          localStorage.setItem(RESTORE_KEY, JSON.stringify(snap));
          return true;
        })
        .catch(function () {
          return false;
        });
    }

    // On return from the Plus checkout: if the store cart still matches the
    // snapshot lines, just drop it; otherwise rebuild the saved lines and let the
    // theme re-render the drawer + header via on:cart:change. The snapshot is
    // cleared only after the lines are really back, so a 4xx/5xx (e.g. a unique
    // piece sold out mid-checkout) keeps it for the next load instead of confirming
    // an empty cart.
    function restoreIfNeeded() {
      if (activating || restoring) return;
      let raw;
      try {
        raw = localStorage.getItem(RESTORE_KEY);
      } catch (e) {
        return;
      }
      if (!raw) return;
      let parsed;
      try {
        parsed = JSON.parse(raw);
      } catch (e) {
        clearRestore();
        return;
      }
      // normalize the old array-only snapshot format to the current shape up front
      const saved = Array.isArray(parsed) ? { lines: parsed, note: null, attributes: null } : parsed;
      const lines = saved.lines || [];
      restoring = true;

      function done(notify) {
        restoring = false;
        if (notify) document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, cancelable: false }));
      }

      getCart()
        .then(function (cart) {
          if (cartHasAll(cartLines(cart), lines)) {
            clearRestore(); // cart already holds the saved lines (logged-out, or grown elsewhere): nothing to rebuild
            done(false);
            return;
          }
          return post('clear', {})
            .then(function () {
              if (!lines.length) return null;
              return post('add', { items: lines });
            })
            .then(function () {
              clearRestore(); // lines are back: safe to drop the snapshot now
              // Restore note/attributes best-effort (clear.js leaves the Plus cart's
              // in place). This is secondary, so a failure here must not re-strand items.
              const note = saved.note;
              const attrs = saved.attributes;
              if (note == null && attrs == null) return null;
              return post('update', { note: note || '', attributes: attrs || {} }).catch(function () {});
            })
            .then(function () {
              done(true);
            });
        })
        .catch(function () {
          // clear/add failed: keep the snapshot to retry, but show the real state.
          done(true);
        });
    }

    // reconcile on load; bfcache restores (back button) don't refire, so re-check.
    // The thaw preserves the in-memory `activating` flag set when we left for the
    // Plus checkout, which would short-circuit the restore we came here to run, so
    // clear it first: a persisted pageshow is precisely the moment to reconcile.
    restoreIfNeeded();
    window.addEventListener('pageshow', function (e) {
      if (e.persisted) {
        activating = false;
        restoreIfNeeded();
      }
    });

    return {
      // The snapshot→checkout protocol for a checkout that does NOT touch the
      // store cart before navigating away (the drawer builds a separate
      // Storefront-API cart). `checkout()` returns a promise and navigates on
      // success; `fallback()` runs when the snapshot can't be persisted (do NOT
      // start a cart-replacing checkout without a backup) or the checkout
      // failed — the cart is untouched in both cases, so the snapshot is dropped.
      activate: function (opts) {
        activating = true;
        return snapshot().then(function (saved) {
          if (!saved) {
            opts.fallback();
            return;
          }
          return Promise.resolve()
            .then(opts.checkout)
            .catch(function () {
              clearRestore();
              opts.fallback();
            });
        });
      },
      // The snapshot→checkout protocol for the Plus landing flow: REPLACE the
      // store cart with only `items` and go to checkout. If the snapshot can't
      // be persisted the cart is never cleared and `fallback()` runs instead.
      // A failure after the clear may leave the cart empty, so reload and let
      // the load-time reconcile rebuild it from the snapshot.
      replaceAndCheckout: function (opts) {
        activating = true;
        return snapshot().then(function (saved) {
          if (!saved) {
            opts.fallback();
            return;
          }
          return post('clear', {})
            .then(function () {
              return post('add', { items: opts.items });
            })
            .then(function () {
              window.location.href = '/checkout';
            })
            .catch(function () {
              window.location.reload();
            });
        });
      }
    };
  })();
